import { useTranslation } from 'react-i18next'

import { formatUsd } from '#/shared/lib/format'
import { cn } from '#/shared/lib/utils'

import { PAYMENT_BALANCE_MODE } from '../lib/payment-formula'
import type {
  PaymentBalanceMode,
  PaymentFormulaResult,
} from '../lib/payment-formula'
import { paymentOptionLabels } from '../lib/payment-options'

interface PaymentSettlementBannerProps {
  formula: PaymentFormulaResult
  values: {
    paymentMethod?: string | null
    paymentTerms?: string | null
    balanceMethod?: string | null
    balanceTime?: string | null
    balanceTermBegins?: string | null
  }
}

const TONE: Record<
  PaymentBalanceMode,
  { container: string; bar: string; secondary: string }
> = {
  cod_only: {
    container: 'bg-blue-50',
    bar: 'bg-blue-500',
    secondary: 'text-blue-500',
  },
  we_owe_carrier: {
    container: 'bg-orange-100',
    bar: 'bg-orange-700',
    secondary: 'text-orange-700',
  },
  carrier_owes_us: {
    container: 'bg-green-50',
    bar: 'bg-green-500',
    secondary: 'text-green-500',
  },
  none: {
    container: 'bg-gray-50',
    bar: 'bg-gray-300',
    secondary: 'text-gray-500',
  },
}

function formatMoney(amount: number): string {
  return formatUsd(amount, { showCents: true })
}

function fallback(value: string | undefined, dash = '—'): string {
  return value && value.length > 0 ? value : dash
}

export function PaymentSettlementBanner({
  formula,
  values,
}: PaymentSettlementBannerProps) {
  const { t } = useTranslation()
  const labels = paymentOptionLabels(t)
  const codAmount = formatMoney(formula.dueOnDelivery)
  const balanceAmount = formatMoney(formula.balanceAbsolute)
  const tone = TONE[formula.mode]
  const lines = buildLines({
    mode: formula.mode,
    codAmount,
    balanceAmount,
    method: labels.method(values.paymentMethod),
    terms: labels.terms(values.paymentTerms),
    balanceMethod: labels.balanceMethod(values.balanceMethod),
    balanceTime: labels.balanceTime(values.balanceTime),
    trigger: labels.balanceTermBegins(values.balanceTermBegins),
    t,
  })

  if (lines.length === 0) return null
  return (
    <div
      key={formula.mode}
      className={cn(
        'flex animate-slide-down items-stretch gap-2.5 overflow-hidden rounded-md px-3 py-2.5',
        tone.container,
      )}
    >
      <div
        className={cn('w-0.75 shrink-0 self-stretch rounded-sm', tone.bar)}
      />
      <div className="flex flex-col gap-0.5 text-xs">
        <span className="leading-4 text-gray-900">{lines[0]}</span>
        {lines[1] && (
          <span className={cn('leading-normal', tone.secondary)}>
            {lines[1]}
          </span>
        )}
      </div>
    </div>
  )
}

function buildLines({
  mode,
  codAmount,
  balanceAmount,
  method,
  terms,
  balanceMethod,
  balanceTime,
  trigger,
  t,
}: {
  mode: PaymentBalanceMode
  codAmount: string
  balanceAmount: string
  method: string | undefined
  terms: string | undefined
  balanceMethod: string | undefined
  balanceTime: string | undefined
  trigger: string | undefined
  t: ReturnType<typeof useTranslation>['t']
}): string[] {
  if (mode === PAYMENT_BALANCE_MODE.NONE) return []
  if (mode === PAYMENT_BALANCE_MODE.COD_ONLY) {
    return [
      t('paymentSettlement.codCollect', {
        amount: codAmount,
        method: fallback(method),
        terms: fallback(terms),
      }),
      t('paymentSettlement.codNoBalance'),
    ]
  }
  if (mode === PAYMENT_BALANCE_MODE.WE_OWE_CARRIER) {
    return [
      t('paymentSettlement.paidUpfront'),
      t('paymentSettlement.settlementWeOwe', {
        amount: balanceAmount,
        method: fallback(balanceMethod),
        time: fallback(balanceTime),
        trigger: fallback(trigger),
      }),
    ]
  }
  return [
    t('paymentSettlement.codCollect', {
      amount: codAmount,
      method: fallback(method),
      terms: fallback(terms),
    }),
    t('paymentSettlement.settlementCarrierOwes', {
      amount: balanceAmount,
      method: fallback(balanceMethod),
      time: fallback(balanceTime),
      trigger: fallback(trigger),
    }),
  ]
}
